import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { aiApi, projectsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Send, Sparkles, Image, Video, Loader2, User, Bot, Download, AlertCircle, Plus, MessageSquare, Trash2, Clock } from 'lucide-react';
import { useClient } from '../context/ClientContext';
import { useTheme } from '../context/ThemeContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const AIStudioPage = () => {
  const { selectedClientId } = useClient();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('chat');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  // Session history state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Image state
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);

  // Video state
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);

  const clientId = selectedClientId !== 'all' ? selectedClientId : undefined;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    const fetchProjects = async () => {
      try { const r = await projectsApi.getAll(clientId); setProjects(r.data); } catch {}
    };
    fetchProjects();
  }, [clientId]);

  // Load chat sessions on mount
  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const r = await aiApi.getChatSessions();
      setSessions(r.data);
    } catch {}
    setSessionsLoading(false);
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Load a specific session's messages
  const loadSession = async (sid) => {
    setSessionId(sid);
    setChatMessages([]);
    try {
      const r = await aiApi.getChatHistory(sid);
      const msgs = [];
      for (const h of r.data) {
        msgs.push({ role: 'user', content: h.user_message });
        msgs.push({ role: 'assistant', content: h.ai_response });
      }
      setChatMessages(msgs);
    } catch {
      toast.error('Error al cargar conversacion');
    }
  };

  // Start a new conversation
  const startNewChat = () => {
    setSessionId(null);
    setChatMessages([]);
    setSelectedProject('');
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await aiApi.deleteChatSession(sid);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success('Conversacion eliminada');
    } catch { toast.error('Error'); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await aiApi.chat(userMessage, sessionId, selectedProject || undefined, clientId);
      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      fetchSessions();
    } catch {
      toast.error('Error al obtener respuesta de IA');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, tuve un error. Por favor intenta de nuevo.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Image session history
  const [imageSessions, setImageSessions] = useState([]);

  const fetchImageSessions = useCallback(async () => {
    try { const r = await aiApi.getImageSessions(); setImageSessions(r.data); } catch {}
  }, []);

  useEffect(() => { if (activeTab === 'image') fetchImageSessions(); }, [activeTab, fetchImageSessions]);

  const handleGenerateImage = async (e) => {
    e.preventDefault();
    if (!imagePrompt.trim() || imageLoading) return;
    setImageLoading(true);
    try {
      const response = await aiApi.generateImage(imagePrompt);
      if (response.data.images && response.data.images.length > 0) {
        setGeneratedImages(prev => [{ prompt: imagePrompt, images: response.data.images, text: response.data.text, session_id: response.data.session_id }, ...prev]);
        toast.success('Imagen generada!');
        fetchImageSessions();
      } else {
        toast.info(response.data.text || 'No se genero imagen');
      }
    } catch { toast.error('Error al generar imagen'); }
    finally { setImageLoading(false); }
  };

  const deleteImageSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await aiApi.deleteImageSession(sid);
      setImageSessions(prev => prev.filter(s => s.session_id !== sid));
      toast.success('Historial eliminado');
    } catch { toast.error('Error'); }
  };

  const handleGenerateVideo = async (e) => {
    e.preventDefault();
    if (!videoPrompt.trim() || videoLoading) return;
    setVideoLoading(true);
    try {
      const response = await aiApi.generateVideo(videoPrompt);
      toast.info(response.data.message);
    } catch { toast.error('Error al generar video'); }
    finally { setVideoLoading(false); }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString('es-MX', { weekday: 'short' });
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  return (
    <DashboardLayout title="Estudio IA">
      <div className="space-y-6" data-testid="ai-studio-page">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={cn('grid w-full max-w-md grid-cols-3', isDark ? 'bg-[#1A1A1A]' : 'bg-zinc-100')}>
            <TabsTrigger value="chat" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-chat">
              <Sparkles size={16} className="mr-2" />Chat
            </TabsTrigger>
            <TabsTrigger value="image" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-image">
              <Image size={16} className="mr-2" />Imagen
            </TabsTrigger>
            <TabsTrigger value="video" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-video">
              <Video size={16} className="mr-2" />Video
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-6">
            <div className="flex gap-4" style={{ minHeight: '520px' }}>
              {/* Sessions sidebar */}
              <div className={cn('w-64 rounded-xl border flex-shrink-0 flex flex-col', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
                <div className="p-3 flex items-center justify-between border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#e4e4e7' }}>
                  <span className={cn('text-sm font-medium', isDark ? 'text-zinc-300' : 'text-zinc-700')}>Conversaciones</span>
                  <Button variant="ghost" size="icon" onClick={startNewChat} className="h-7 w-7" data-testid="new-chat-btn">
                    <Plus size={16} className="text-orange-400" />
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {sessionsLoading ? (
                    <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-zinc-500" /></div>
                  ) : sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare size={24} className="mx-auto mb-2 text-zinc-600" />
                      <p className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Sin conversaciones</p>
                    </div>
                  ) : sessions.map(s => (
                    <button
                      key={s.session_id}
                      onClick={() => loadSession(s.session_id)}
                      className={cn(
                        'w-full text-left p-2.5 rounded-lg transition-colors group flex items-start gap-2',
                        sessionId === s.session_id
                          ? (isDark ? 'bg-orange-500/15 border border-orange-500/30' : 'bg-orange-50 border border-orange-200')
                          : (isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-50')
                      )}
                      data-testid={`session-${s.session_id}`}
                    >
                      <MessageSquare size={14} className="mt-0.5 flex-shrink-0 text-zinc-500" />
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-medium truncate', isDark ? 'text-zinc-200' : 'text-zinc-800')}>{s.last_message || 'Conversacion'}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock size={10} className="text-zinc-500" />
                          <span className="text-[10px] text-zinc-500">{formatTime(s.last_at)}</span>
                          <span className="text-[10px] text-zinc-600 ml-auto">{s.message_count} msgs</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteSession(s.session_id, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 transition-opacity"
                        data-testid={`delete-session-${s.session_id}`}
                      >
                        <Trash2 size={12} className="text-red-400" />
                      </button>
                    </button>
                  ))}
                </div>
              </div>

              {/* Chat area */}
              <Card className={cn('flex-1 flex flex-col', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}>
                      <Sparkles className="text-orange-400" size={20} />
                      Asistente IA (GPT-4o)
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  {/* Chat Messages */}
                  <div className={cn('flex-1 overflow-y-auto mb-4 space-y-4 p-4 rounded-lg', isDark ? 'bg-[#1A1A1A]' : 'bg-zinc-50')} style={{ minHeight: '340px', maxHeight: '400px' }}>
                    {chatMessages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                        <Bot size={48} className="mb-4" />
                        <p>Inicia una conversacion con el asistente IA</p>
                        <p className="text-sm">Pregunta sobre proyectos, ideas de contenido o consejos de negocio</p>
                      </div>
                    ) : (
                      chatMessages.map((msg, index) => (
                        <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          {msg.role === 'assistant' && (
                            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                              <Bot size={16} className="text-orange-400" />
                            </div>
                          )}
                          <div className={cn('max-w-[80%] p-4 rounded-2xl', msg.role === 'user'
                            ? 'bg-orange-500 text-white'
                            : (isDark ? 'bg-[#141414] border border-white/5 text-zinc-300' : 'bg-white border border-zinc-200 text-zinc-700'))}>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          {msg.role === 'user' && (
                            <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                              <User size={16} className="text-zinc-300" />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {chatLoading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Loader2 size={16} className="text-orange-400 animate-spin" />
                        </div>
                        <div className={cn('p-4 rounded-2xl border', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
                          <div className="flex gap-1">
                            <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat Input */}
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Escribe tu mensaje..."
                      className={cn('flex-1', isDark ? 'bg-[#1A1A1A] border-transparent' : 'bg-zinc-50 border-zinc-200')}
                      disabled={chatLoading}
                      data-testid="chat-input"
                    />
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={chatLoading || !chatInput.trim()} data-testid="chat-send">
                      {chatLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Image Tab */}
          <TabsContent value="image" className="mt-6">
            <div className="flex gap-4" style={{ minHeight: '520px' }}>
              {/* Image History Sidebar */}
              <Card className={cn('w-64 shrink-0 hidden lg:block', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <p className={cn('text-sm font-medium', isDark ? 'text-zinc-300' : 'text-zinc-700')}>Historial</p>
                    <Button variant="ghost" size="icon" onClick={() => { setGeneratedImages([]); setImagePrompt(''); }} className="h-7 w-7 text-orange-400" data-testid="new-image-btn"><Plus size={16} /></Button>
                  </div>
                  <div className="space-y-1 max-h-[440px] overflow-y-auto">
                    {imageSessions.map(s => (
                      <div key={s.session_id} className={cn('flex items-center gap-2 px-2 py-2 rounded-lg text-xs cursor-pointer group', isDark ? 'hover:bg-white/5 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600')}>
                        <Clock size={12} className="shrink-0 text-pink-400" />
                        <span className="truncate flex-1">{s.prompt?.substring(0, 30)}...</span>
                        <button onClick={(e) => deleteImageSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
                      </div>
                    ))}
                    {imageSessions.length === 0 && <p className="text-xs text-zinc-500 text-center py-4">Sin historial</p>}
                  </div>
                </CardContent>
              </Card>

              {/* Image generation main area */}
              <Card className={cn('flex-1', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
                <CardHeader>
                  <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}>
                    <Image className="text-pink-400" size={20} />
                    Generacion de Imagenes (Nano Banana)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGenerateImage} className="space-y-4">
                    <Textarea value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="Describe la imagen que quieres generar..." className={cn('min-h-[80px]', isDark ? 'bg-[#1A1A1A] border-transparent' : 'bg-zinc-50 border-zinc-200')} disabled={imageLoading} data-testid="image-prompt" />
                    <Button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white" disabled={imageLoading || !imagePrompt.trim()} data-testid="image-generate">
                      {imageLoading ? <><Loader2 size={20} className="mr-2 animate-spin" />Generando...</> : <><Sparkles size={20} className="mr-2" />Generar Imagen</>}
                    </Button>
                  </form>
                  {generatedImages.length > 0 && (
                    <div className="mt-6 space-y-4 max-h-[300px] overflow-y-auto">
                      {generatedImages.map((gen, index) => (
                        <div key={index} className={cn('p-3 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                          <p className="text-xs text-zinc-400 mb-2">"{gen.prompt}"</p>
                          {gen.text && <p className={cn('text-xs mb-2', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{gen.text}</p>}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {gen.images.map((img, imgIndex) => (
                              <div key={imgIndex} className="relative group">
                                <img src={`data:${img.mime_type};base64,${img.full_data}`} alt={`Generada ${imgIndex + 1}`} className="w-full rounded-lg" />
                                <a href={`data:${img.mime_type};base64,${img.full_data}`} download={`imagen-${index}-${imgIndex}.png`} className="absolute bottom-2 right-2 p-2 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"><Download size={16} /></a>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Video Tab */}
          <TabsContent value="video" className="mt-6">
            <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
              <CardHeader>
                <CardTitle className={cn('flex items-center gap-2', isDark ? 'text-white' : 'text-zinc-900')}>
                  <Video className="text-amber-400" size={20} />
                  Generacion de Video (Veo 3)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
                  <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-amber-200 font-medium">Funcion Simulada</p>
                    <p className="text-amber-200/70 text-sm">La generacion de video con Veo 3 requiere una API key de Google. Esta funcion esta actualmente simulada.</p>
                  </div>
                </div>
                <form onSubmit={handleGenerateVideo} className="space-y-4">
                  <Textarea value={videoPrompt} onChange={(e) => setVideoPrompt(e.target.value)} placeholder="Describe el video que quieres generar..." className={cn('min-h-[100px]', isDark ? 'bg-[#1A1A1A] border-transparent' : 'bg-zinc-50 border-zinc-200')} disabled={videoLoading} data-testid="video-prompt" />
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={videoLoading || !videoPrompt.trim()} data-testid="video-generate">
                    {videoLoading ? <><Loader2 size={20} className="mr-2 animate-spin" />Procesando...</> : <><Video size={20} className="mr-2" />Generar Video (Simulado)</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AIStudioPage;
